/**
 * @file Loader de zone de contenu avec délai anti-flash et centrage mesuré.
 * @module packages.common.components.loading-screen.content-loader
 *
 * Loader canonique du chargement de données d'une page : rond, discret,
 * centré dans la zone de contenu — le layout (header, navigation) reste
 * visible. Deux garanties :
 * - Centrage réel : le conteneur mesure sa position au montage et remplit
 *   exactement l'espace visible restant (100dvh − top), le spinner est donc
 *   au centre optique de la zone de contenu quel que soit le parent.
 * - Anti-flash : le spinner n'apparaît qu'après un délai ; un loader visible
 *   30 ms sur un chargement rapide (cache SWR, chunk déjà téléchargé) est
 *   plus dérangeant que 200 ms de zone vide.
 */

import {useEffect, useLayoutEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
// @mui
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';

/** Marge basse conservée sous le loader (px), symétrique du padding du layout. */
const BOTTOM_GUTTER = 24;

/**
 * Loader rond centré dans l'espace visible restant de la page.
 *
 * @param {Object} props - Props du composant
 * @param {number} [props.delay=200] - Délai d'apparition du spinner en ms (0 = immédiat)
 * @param {number} [props.size=36] - Taille du CircularProgress en pixels
 * @param {string} [props.message] - Message optionnel affiché sous le spinner
 * @param {Object} [props.sx] - Styles MUI supplémentaires du conteneur
 * @return {JSX.Element} Le composant ContentLoader
 */
export default function ContentLoader({delay = 200, size = 36, message, sx, ...other}) {
  const containerRef = useRef(null);
  const [visible, setVisible] = useState(delay === 0);
  const [fillHeight, setFillHeight] = useState(null);

  // Mesure la position réelle du conteneur pour occuper exactement l'espace
  // visible sous le header — c'est ce qui garantit un centrage optique juste
  useLayoutEffect(() => {
    if (!containerRef.current) return;
    const {top} = containerRef.current.getBoundingClientRect();
    setFillHeight(`calc(100dvh - ${Math.max(0, Math.round(top))}px - ${BOTTOM_GUTTER}px)`);
  }, []);

  useEffect(() => {
    if (delay === 0) return undefined;
    const timer = setTimeout(() => setVisible(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  return (
    <Box
      ref={containerRef}
      sx={{
        flexGrow: 1,
        width: '100%',
        minHeight: fillHeight || '50vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        ...sx,
      }}
      {...other}
    >
      {visible && (
        <>
          <CircularProgress size={size} />
          {message && (
            <Typography variant="body2" color="text.secondary">
              {message}
            </Typography>
          )}
        </>
      )}
    </Box>
  );
}

ContentLoader.propTypes = {
  delay: PropTypes.number,
  size: PropTypes.number,
  message: PropTypes.string,
  sx: PropTypes.object,
};
